import {
  getMachinesSearchTaggingQueryKey,
  postGameStartMutation,
  type SportType,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Spinner, toast } from "@win-reality/react-ui";
import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { PlayerSelectModal } from "@/pages/Tagging/GameSetup/PlayerSelectModal";
import { toStartGameLineup } from "@/pages/Tagging/GameSetup/startGameLineup";
import { TeamLineupView } from "@/pages/Tagging/GameSetup/TeamLineupPanel";
import { TeamPanel } from "@/pages/Tagging/GameSetup/TeamPanel";
import {
  type TeamSelectData,
  TeamSelectModal,
} from "@/pages/Tagging/GameSetup/TeamSelectModal";
import type { IPlayer, TTeamSides } from "@/pages/Tagging/store/types";
import { handleErrorNotification } from "@/pages/Tagging/utils/handleErrorNotification";

import {
  toSportType,
  useActiveGame,
} from "../Scorekeeping/hooks/useActiveGame";
import { useGameSetupState } from "./useGameSetupState";

type PlayerType = "pitcher" | "catcher";

const SIDES: TTeamSides[] = ["away", "home"];

/**
 * Game setup for Scorekeeping V2: pick both teams and their lineups, then
 * start the game through omnibus and open it. Uses the legacy setup's
 * components on local state, so it shares nothing with the legacy tagger's
 * store.
 */
const GameSetup = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: machineId } = useParams() as { id: string };
  const { gameId, fieldSport, isLoading } = useActiveGame();
  const [teams, dispatch] = useGameSetupState();
  // The game service takes the sport from the start request, and Scorekeeping
  // treats a game without one as baseball. The field's configured sport wins,
  // as in instant start and scheduling; for a field without one, the last
  // picked team with a known sport sets it, like the legacy setup.
  const [teamSport, setTeamSport] = useState<SportType | null>(null);
  const sport = fieldSport ?? teamSport;

  const [teamSelectModal, setTeamSelectModal] = useState<{
    isOpen: boolean;
    side: TTeamSides;
  }>({ isOpen: false, side: "away" });
  const [playerSelectModal, setPlayerSelectModal] = useState<{
    isOpen: boolean;
    side: TTeamSides;
    type: PlayerType;
  }>({ isOpen: false, side: "away", type: "pitcher" });

  const gamePath = `/tagging-v2/${machineId}/game`;

  const { mutate: startGame, isPending } = useMutation({
    ...postGameStartMutation(),
    onSuccess: async () => {
      // The game page finds the new game on the machine
      await queryClient.refetchQueries({
        queryKey: getMachinesSearchTaggingQueryKey(),
      });
      navigate(gamePath, { replace: true });
    },
    onError: (error) =>
      handleErrorNotification(
        "An error occurred while trying to start the game",
        error,
      ),
  });

  if (isLoading) {
    return (
      <div
        className="flex h-full w-full items-center justify-center"
        data-testid="game-setup-loading-status"
      >
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  // A game is already underway (e.g. a refresh after starting it)
  if (gameId) return <Navigate to={gamePath} replace />;

  const handleTeamSelect = (data: TeamSelectData) => {
    const selectedSport = toSportType(data.sport);
    if (selectedSport) setTeamSport(selectedSport);
    dispatch({
      type: "setTeam",
      side: teamSelectModal.side,
      name: data.teamName,
      uuid: data.teamUuid,
      roster: data.teamRoster,
    });
    setTeamSelectModal((modal) => ({ ...modal, isOpen: false }));
  };

  const handlePlayerSelect = (player: IPlayer) => {
    const { side, type } = playerSelectModal;
    dispatch({
      type: type === "pitcher" ? "setPitcher" : "setCatcher",
      side,
      player,
    });
  };

  const handleStartGame = () => {
    for (const side of SIDES) {
      if (teams[side].roster.length === 0) {
        return toast(
          `${side === "away" ? "Away" : "Home"} team needs at least one player on the roster`,
          { variant: "error" },
        );
      }
    }

    startGame({
      body: {
        awayTeamId: teams.away.uuid,
        awayTeamLineup: toStartGameLineup(teams.away),
        homeTeamId: teams.home.uuid,
        homeTeamLineup: toStartGameLineup(teams.home),
        machineUuid: machineId,
        type: "game",
        ...(sport && { sport }),
      },
    });
  };

  const openTeamSelect = (side: TTeamSides) =>
    setTeamSelectModal({ isOpen: true, side });

  const renderTeam = (side: TTeamSides) =>
    teams[side].name ? (
      <TeamLineupView
        side={side}
        team={teams[side]}
        onEditTeam={() => openTeamSelect(side)}
        onSelectPlayer={(type) =>
          setPlayerSelectModal({ isOpen: true, side, type })
        }
        onClearTeam={() => dispatch({ type: "clearTeam", side })}
        onClearPitcher={() => dispatch({ type: "clearPitcher", side })}
        onClearCatcher={() => dispatch({ type: "clearCatcher", side })}
        onAddToLineup={(player) =>
          dispatch({ type: "addToLineup", side, player })
        }
        onRemoveFromLineup={(player) =>
          dispatch({ type: "removeFromLineup", side, player })
        }
        onReorderLineup={(lineup) =>
          dispatch({ type: "reorderLineup", side, lineup })
        }
      />
    ) : (
      <TeamPanel
        side={side}
        teamName={teams[side].name}
        onSelectTeam={() => openTeamSelect(side)}
      />
    );

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Main content - split screen */}
      <div className="flex flex-1 overflow-hidden">
        {renderTeam("away")}

        {/* Divider */}
        <div className="w-px bg-gray-200" />

        {renderTeam("home")}
      </div>

      {/* Start Game Footer */}
      <div className="flex justify-center px-6 py-4 shadow-scroller-top">
        <Button
          variant="secondary-flat-light"
          onClick={handleStartGame}
          isLoading={isPending}
          aria-busy={isPending}
          className="px-12"
          data-testid="game-setup-start-game-button"
        >
          Start Game
        </Button>
      </div>

      <TeamSelectModal
        isOpen={teamSelectModal.isOpen}
        onClose={() =>
          setTeamSelectModal((modal) => ({ ...modal, isOpen: false }))
        }
        onSubmit={handleTeamSelect}
        title={`Select ${teamSelectModal.side === "away" ? "Away" : "Home"} Team`}
      />

      <PlayerSelectModal
        isOpen={playerSelectModal.isOpen}
        onClose={() =>
          setPlayerSelectModal((modal) => ({ ...modal, isOpen: false }))
        }
        onSubmit={handlePlayerSelect}
        roster={teams[playerSelectModal.side].roster}
        type={playerSelectModal.type}
      />
    </div>
  );
};

export default GameSetup;
